import dayjs from "dayjs";
import MarkedReact from "marked-react";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BoardAiApi, BoardAiMarkdownResult } from "../../../services/board-ai-api";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaNumber from "../../ui-kit/kuika-number/kuika-number";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../ui-kit/kuika-tooltip/kuika-tooltip";
import AiSparkleIcon from "./ai-sparkle-icon";
import { formatUnreadCount } from "./board-nav-badge";
import {
  connectNotificationStream,
  getNotifications,
  markNotificationsRead,
  refreshNotifications,
  subscribeNotifications,
  unreadNotificationCount
} from "./notifications";
import "./notifications-panel.scss";
import { displayNameOf } from "./people-directory";
import { SAFE_MARKDOWN_RENDERER } from "./safe-markdown";
import { BoardNotification } from "./types";
import { useBoardAiEnabled } from "./use-board-ai";

const NotificationsPanel: React.FC = () => {
  const navigate = useNavigate();
  const { domainName } = useParams<{ domainName: string }>();
  const [open, setOpen] = useState<boolean>(false);
  const [items, setItems] = useState<BoardNotification[]>(() => getNotifications());
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const aiEnabled: boolean = useBoardAiEnabled();
  const [digest, setDigest] = useState<string | null>(null);
  const [digestBusy, setDigestBusy] = useState<boolean>(false);

  useEffect(() => {
    const unsubscribe: () => void = subscribeNotifications(() => setItems(getNotifications()));
    void refreshNotifications();
    connectNotificationStream();
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent): void => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  const unread: number = unreadNotificationCount();

  const handleOpen = (): void => {
    const willOpen: boolean = !open;
    setOpen(willOpen);
    if (willOpen && unread > 0) {
      void markNotificationsRead();
    }
  };

  const goTo = (entry: BoardNotification): void => {
    if (!entry.issueKey) return;
    setOpen(false);
    navigate(`/${domainName}/issue/${entry.issueKey}`);
  };

  const handleRecap = (): void => {
    if (digestBusy) return;
    setDigestBusy(true);
    void BoardAiApi.digest()
      .then((result: BoardAiMarkdownResult): void => setDigest(result.markdown))
      .catch((): void => undefined)
      .finally((): void => setDigestBusy(false));
  };

  return (
    <div ref={wrapperRef} className="kuika-board-notifications">
      <KuikaTooltip content="Notifications" position="bottom">
        <KuikaButton
          type="tertiary"
          color="neutral"
          size="sm"
          isIcon
          onClick={handleOpen}
          data-testid="board-notifications-button"
        >
          <KuikaSvg name="bell" width={16} height={16} />
        </KuikaButton>
      </KuikaTooltip>
      {unread > 0 && (
        <span className="kuika-board-notifications__badge">
          <KuikaNumber value={unread} format={formatUnreadCount} />
        </span>
      )}
      {open && (
        <div className="kuika-board-notifications__panel" role="menu">
          <div className="kuika-board-notifications__header">
            <span className="kuika-board-notifications__title">Inbox</span>
            <span className="kuika-board-notifications__header-right">
              {aiEnabled && items.length > 0 && (
                <button
                  type="button"
                  className="kuika-board__ai-action"
                  onClick={handleRecap}
                  disabled={digestBusy}
                  title="Summarize recent activity"
                >
                  <AiSparkleIcon size={12} className={digestBusy ? "kuika-board__ai-spin" : undefined} />
                  {digestBusy ? "Summarizing…" : "Recap"}
                </button>
              )}
              <span className="kuika-board-notifications__meta">
                {items.length} event{items.length === 1 ? "" : "s"}
              </span>
            </span>
          </div>
          {digest && (
            <div className="kuika-board__ai-card kuika-board-notifications__digest">
              <div className="kuika-board__ai-card-label">
                <AiSparkleIcon size={12} />
                Recap
              </div>
              <div className="kuika-board__ai-card-body">
                <MarkedReact gfm breaks value={digest} renderer={SAFE_MARKDOWN_RENDERER} />
              </div>
              <button
                type="button"
                className="kuika-board__ai-card-dismiss"
                onClick={() => setDigest(null)}
                aria-label="Dismiss recap"
              >
                ×
              </button>
            </div>
          )}
          {items.length === 0 ? (
            <div className="kuika-board-notifications__empty">
              Nothing yet. Activity by others on issues you own, report, or watch shows up here.
            </div>
          ) : (
            items.map((entry: BoardNotification) => {
              if (!entry.issueKey) return null;
              const isUnread: boolean = !entry.read;
              return (
                <button
                  key={entry.id}
                  type="button"
                  className={`kuika-board-notifications__item${isUnread ? " kuika-board-notifications__item--unread" : ""}`}
                  onClick={() => goTo(entry)}
                >
                  <span className="kuika-board-notifications__key">{entry.issueKey}</span>
                  <span className="kuika-board-notifications__text">
                    <strong>{entry.actorUserId ? displayNameOf(entry.actorUserId) : "Automation"}</strong> {entry.text}
                  </span>
                  <span className="kuika-board-notifications__time">{dayjs(entry.at).format("MMM D, HH:mm")}</span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationsPanel;
